import {
  createContext,
  type PropsWithChildren,
  useContext,
  useMemo,
} from "react";
import type {
  GridFilterModel,
  GridSortModel,
  GridValidRowModel,
} from "@mui/x-data-grid-premium";
import type { GenericAbortSignal } from "axios";
import type { ValidSaveFormResponseData } from "src/components/Form/hooks/useResourceForm/useResourceForm.tsx";

export type ValidGetListResponse<
  Row extends GridValidRowModel = GridValidRowModel,
> = {
  data: Row[];
  meta: {
    total: number;
  };
  [key: string]: any;
};

export type DataProvider = {
  getList: (params: {
    resource: string;
    option?: string;
    include?: string[];
    useSimplePagination?: boolean;
    page?: {
      number?: number;
      size?: number;
    };

    sort?: GridSortModel;
    filter?: GridFilterModel;
    groupKeys?: string[];
    signal?: GenericAbortSignal;
    locale?: string;
  }) => Promise<ValidGetListResponse>;
  deleteOne: (params: { resource: string; id: string }) => Promise<any>;
  createOne: <
    ResponseData extends ValidSaveFormResponseData = ValidSaveFormResponseData,
  >(params: {
    resource: string;
    data: any;
    meta?: any;
    locale: string;
    additionalHeaders?: Record<string, any>;
  }) => Promise<ResponseData>;
  updateOne: <
    ResponseData extends ValidSaveFormResponseData = ValidSaveFormResponseData,
  >(params: {
    resource: string;
    data: any;
    id: string;
    tab?: string;
    meta?: any;
    locale: string;
    additionalHeaders?: Record<string, any>;
  }) => Promise<ResponseData>;
  getOne: (params: {
    resource: string;
    include?: string[];
    meta?: any;
    signal?: GenericAbortSignal;
    id: string;
    locale?: string;
  }) => Promise<any>;
  getCreateFormData: (params: {
    resource: string;
    meta?: any;
    signal?: GenericAbortSignal;
    requestParams?: Record<string, any>;
    locale?: string;
  }) => Promise<any>;
  cloneToLocale: (params: {
    resource: string;
    from_locale: string;
    to_locale: string;
    id: string;
  }) => Promise<any>;
};

type SystemContextValue = {
  dataProvider: DataProvider;
};

export const SystemContext = createContext<null | SystemContextValue>(null);

export const SystemProvider = ({
  dataProvider,
  children,
}: PropsWithChildren<SystemContextValue>) => {
  const context = useMemo(
    () => ({
      dataProvider,
    }),
    [dataProvider],
  );
  return (
    <SystemContext.Provider value={context}>{children}</SystemContext.Provider>
  );
};

export const useSystemProvider = () => {
  const context = useContext(SystemContext);
  if (!context) {
    throw new Error("useSystemProvider must be used within SystemProvider");
  }
  return context;
};
